import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Trash2, Copy, ExternalLink, ShieldAlert, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { listMyScans, deleteScan } from "@/lib/card-scanner.functions";
import { scanRowSummary } from "@/lib/card-scanner/pro";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { buildAuctionSlug } from "@/lib/auction-slug";
import { formatGradingBadge } from "@/lib/grading/companies";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/_authenticated/seller/scan-history")({
  head: () => ({ meta: [{ title: "Scan history — BidNova Auctions" }] }),
  component: ScanHistoryPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

type ScanRow = {
  id: string;
  created_at: string;
  detected_game: string | null;
  primary_match: Record<string, unknown> | null;
  confidences: { overall?: number | null } | null;
  condition_suggestion: { category?: string | null } | null;
  authenticity: { warning?: boolean | null } | null;
  slab: { grading_company?: string | null; grade?: string | null } | null;
  seo_title: string | null;
  tags: string[] | null;
  confirmed: boolean;
  auction_id: string | null;
  processed_thumbnail: string | null;
  seller_edits: Record<string, unknown> | null;
};

function ScanHistoryPage() {
  const { t } = useTranslation("settings");
  const list = useServerFn(listMyScans);
  const del = useServerFn(deleteScan);
  const q = useQuery({
    queryKey: ["my-scans"],
    queryFn: () => list(),
  });

  async function onDelete(id: string) {
    if (!confirm(t("scanHistory.deleteConfirm", "Delete this scan? This can't be undone."))) return;
    try {
      await del({ data: { scan_id: id } });
      toast.success(t("scanHistory.deleteSuccess", "Scan deleted"));
      await q.refetch();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  const rows = (q.data ?? []) as ScanRow[];

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-gold">{t("scanHistory.eyebrow", "AI Scanner")}</p>
          <h1 className="mt-2 font-display text-3xl">{t("scanHistory.title", "Scan history")}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t("scanHistory.subtitle", "Every scan you've run, with the recognised card, confidence, condition suggestion and any authenticity notes.")}
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/sell">{t("scanHistory.newListing", "New listing")}</Link>
        </Button>
      </div>

      {q.isLoading && <p className="text-muted-foreground">{t("scanHistory.loading", "Loading…")}</p>}
      {!q.isLoading && rows.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t("scanHistory.empty", "No scans yet. Start a listing to scan your first card with AI.")}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((r) => {
          const s = scanRowSummary(r);
          const overall = r.confidences?.overall ?? null;
          const grading = formatGradingBadge({
            card_type: r.slab?.grading_company ? "graded" : "raw",
            grading_company: r.slab?.grading_company ?? null,
            grade: r.slab?.grade ?? null,
          });
          return (
            <Card key={r.id} className="overflow-hidden">
              <div className="aspect-[3/4] bg-muted/40 flex items-center justify-center">
                {r.processed_thumbnail ? (
                  <img src={r.processed_thumbnail} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Sparkles className="h-8 w-8 text-muted-foreground/40" />
                )}
              </div>
              <CardHeader className="pb-2">
                <CardTitle className="text-base leading-tight">{s.title}</CardTitle>
                <p className="text-xs text-muted-foreground line-clamp-1">{s.sub}</p>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                <div className="flex flex-wrap gap-1">
                  <Badge variant="outline">{grading}</Badge>
                  <Badge variant="outline">
                    {r.condition_suggestion?.category ?? t("scanHistory.conditionFallback", "Condition —")}
                  </Badge>
                  <Badge variant="outline">
                    {overall == null ? "—" : `${Math.round(overall * 100)}%`}
                  </Badge>
                  {r.authenticity?.warning ? (
                    <Badge variant="destructive" className="gap-1">
                      <ShieldAlert className="h-3 w-3" /> {t("scanHistory.verifyBadge", "Verify")}
                    </Badge>
                  ) : null}
                  {r.confirmed ? (
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40 gap-1">
                      <CheckCircle2 className="h-3 w-3" /> {t("scanHistory.confirmedBadge", "Confirmed")}
                    </Badge>
                  ) : null}
                </div>
                <div className="text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()}
                </div>
                <div className="flex gap-2 pt-1">
                  <Button asChild size="sm" variant="outline" className="flex-1">
                    <a href={`/sell?scan_id=${r.id}`}>
                      <Copy className="h-3 w-3 mr-1" /> {t("scanHistory.duplicateBtn", "Duplicate")}
                    </a>
                  </Button>
                  {r.auction_id ? (
                    <Button asChild size="sm" variant="outline">
                      <Link
                        to="/auction/$slug"
                        params={{
                          slug: buildAuctionSlug({
                            id: r.auction_id,
                            title: r.seo_title ?? s.title,
                            grading_company: r.slab?.grading_company,
                            grade: r.slab?.grade,
                          }),
                        }}
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    </Button>
                  ) : null}
                  <Button size="sm" variant="ghost" onClick={() => onDelete(r.id)}>
                    <Trash2 className="h-3 w-3 text-red-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
